import { useCallback, useState } from 'react';
import {
  Stack,
  Typography,
  Divider,
  useMediaQuery,
  Button,
  IconButton,
  MenuItem,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { useWatch } from 'react-hook-form';
import dayjs from 'dayjs';

import { Iconify, RHFTextField, RHFUploadAvatar, RHFDatePicker, RHFSelect } from '@app/components';
import { Strings, memberSources } from '@app/constants';
import useResponsive from '@app/hooks/use-responsive';
import { BankInformationForm } from '@app/sections/shared/bank-info-form';
import { EducationsInfoForm } from '@app/sections/shared/education-info-form';
import { DocumentsInfoForm } from '@app/sections/shared/document-info-form';
import { WorkExperienceInfoForm } from '@app/sections/shared/work-experience-info-form';
import { relation } from '@app/mock/pesonal-info';

const PersonalInfoForm = ({ methods, data, title, filePickerName }: any) => {
  const [disabled, setDisabled] = useState(data?.samePresentAddress);

  const initialInformation = {
    name: '',
    relation: '',
    phone: '',
  };
  const isMobile = useResponsive('up', 'sm');
  const isDesktop = useMediaQuery((theme: any) => theme.breakpoints.up('sm'));
  const direction = isMobile ? 'row' : 'column';
  const [source] = useWatch({ name: ['source'] });
  const [isImageUploaded, setIsImageUploaded] = useState(false);

  const handleDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0];
      const newFile = Object.assign(file, {
        preview: URL.createObjectURL(file),
      });
      if (file) {
        methods.setValue('personalInfo.employeeImage', newFile);
        setIsImageUploaded(true);
      }
    },
    [methods]
  );

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      methods.setValue('samePresentAddress', e.target.checked);
      methods.setValue(
        'personalInfo.permanentAddress.address',
        methods.getValues('personalInfo.presentAddress.address')
      );
      methods.setValue(
        'personalInfo.permanentAddress.addressLine2',
        methods.getValues('personalInfo.presentAddress.addressLine2')
      );
      methods.setValue(
        'personalInfo.permanentAddress.city',
        methods.getValues('personalInfo.presentAddress.city')
      );
      methods.setValue(
        'personalInfo.permanentAddress.state',
        methods.getValues('personalInfo.presentAddress.state')
      );
      methods.setValue(
        'personalInfo.permanentAddress.country',
        methods.getValues('personalInfo.presentAddress.country')
      );
      methods.setValue(
        'personalInfo.permanentAddress.postalCode',
        methods.getValues('personalInfo.presentAddress.postalCode')
      );
      setDisabled(true);
    } else {
      methods.setValue('samePresentAddress', e.target.checked);
      methods.setValue('personalInfo.permanentAddress.address', '');
      methods.setValue('personalInfo.permanentAddress.addressLine2', '');
      methods.setValue('personalInfo.permanentAddress.city', '');
      methods.setValue('personalInfo.permanentAddress.state', '');
      methods.setValue('personalInfo.permanentAddress.country', '');
      methods.setValue('personalInfo.permanentAddress.postalCode', '');
      setDisabled(false);
    }
  };

  const emergencyContact = data?.emergencyContact;
  const [information, setInformation] = useState(
    emergencyContact ? emergencyContact ?? [] : [initialInformation]
  );

  const updateGraduationState = (updatedEducations?: any) => {
    methods.setValue('educationInfo.graduation', updatedEducations ?? []);
  };

  const handleAddMoreClick = () => {
    setInformation((prevInformation: any) => [...prevInformation, { ...initialInformation }]);
  };

  const handleRemoveClick = (index: number) => {
    const updateInformation = information?.filter((_: any, i: any) => i !== index);
    setInformation(updateInformation);
  };

  const renderInputFieldSets = () => {
    return information?.map((information: any, index: number) => (
      <>
        {index === 0 ? null : (
          <Stack alignItems={'flex-end'}>
            <IconButton
              onClick={() => {
                handleRemoveClick(index);
              }}
              sx={{
                width: '3vw',
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Iconify icon="material-symbols:close" />
            </IconButton>
          </Stack>
        )}
        <Stack flexWrap="wrap">
          <Stack direction={'row'} justifyContent={'space-between'} gap={2} sx={{ width: '100%' }}>
            <RHFTextField
              name={`emergencyContactInfo.emergencyContact[${index}].name`}
              label={`${Strings.addEmployee.name}`}
              sx={{ width: '33%' }}
            />
            <RHFTextField
              name={`emergencyContactInfo.emergencyContact[${index}].relation`}
              label={`${Strings.addEmployee.relation}`}
              select
              sx={{ width: '33%' }}
            >
              {relation.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.name}
                </MenuItem>
              ))}
            </RHFTextField>
            <RHFTextField
              name={`emergencyContactInfo.emergencyContact[${index}].phone`}
              label={`${Strings.addEmployee.phone}`}
              sx={{ width: '33%' }}
            />
          </Stack>
        </Stack>
      </>
    ));
  };

  return (
    <Stack spacing={2}>
      <Stack justifyContent={'center'} spacing={2}>
        <Typography variant="h5">{Strings.addEmployee.personal}</Typography>
        <Divider />
      </Stack>
      <Stack direction={direction} gap={2}>
        <Stack flexGrow={0.2} sx={{ alignItems: isDesktop ? 'center' : 'flex-start' }}>
          <RHFUploadAvatar
            onDrop={handleDrop}
            name={'personalInfo.employeeImage'}
            sx={{
              border:
                isImageUploaded || data?.employeeImage ? 'none' : '5px dashed rgba(0, 0, 0, 0.08)',
            }}
          />
        </Stack>

        <Stack spacing={2} flexGrow={1}>
          <Stack direction={direction} spacing={2}>
            <RHFTextField required name="personalInfo.firstName" label={Strings.field.firstName} />
            <RHFTextField required name="personalInfo.lastName" label={Strings.field.lastName} />

            <RHFTextField
              required
              name="personalInfo.personalEmail"
              label={Strings.field.personalEmail}
            />
          </Stack>

          <Stack direction={direction} spacing={2}>
            <RHFTextField name="personalInfo.phone" label={`${Strings.field.mobileNumber}`} />
            <RHFDatePicker
              name="personalInfo.dob"
              label={Strings.addEmployee.dateOfBirth}
              sx={{ width: '100%' }}
              defaultValue={data?.dob ? dayjs(data?.dob, 'DD/MM/YYYY') : null}
              maxDate={dayjs()}
            />
          </Stack>
          <Stack direction={direction} spacing={2}>
            <RHFSelect name="personalInfo.gender" label={Strings.field.gender}>
              <MenuItem value={Strings.addEmployee.male}>{Strings.addEmployee.male}</MenuItem>
              <MenuItem value={Strings.addEmployee.female}>{Strings.addEmployee.female}</MenuItem>
              <MenuItem value={Strings.addEmployee.other}>{Strings.addEmployee.other}</MenuItem>
            </RHFSelect>
            <RHFSelect name="personalInfo.maritalStatus" label={Strings.addEmployee.maritalStatus}>
              <MenuItem value={Strings.addEmployee.married}>{Strings.addEmployee.married}</MenuItem>
              <MenuItem value={Strings.addEmployee.unmarried}>
                {Strings.addEmployee.unmarried}
              </MenuItem>
            </RHFSelect>
          </Stack>
          <Stack direction={direction} gap={2}>
            <Stack flex={1}>
              <RHFTextField
                name="personalInfo.aboutMe"
                label={Strings.addEmployee.aboutMe}
                multiline
                minRows={2}
              />
            </Stack>
            <Stack flex={1} gap={2}>
              {title === 'intern' && (
                <RHFSelect required name="source" type="select" label={Strings.field.source}>
                  {memberSources.map((option) => (
                    <MenuItem key={option.value} value={option.label}>
                      {option.label}
                    </MenuItem>
                  ))}
                </RHFSelect>
              )}
              {source === 'Reference' && (
                <RHFTextField
                  name="referenceBy"
                  label={Strings.field.enterReferenceName}
                  required
                />
              )}
              {source === 'Other' && (
                <RHFTextField name="other" label={Strings.field.enterOtherSourceName} required />
              )}
            </Stack>
          </Stack>
          <Stack direction={{ sm: 'column', md: 'row' }} gap={2}>
            <Stack gap={2} flex={1}>
              <Stack fontWeight={600}> {Strings.addEmployee.presentAddress}</Stack>
              <Stack direction={direction} spacing={2}>
                <RHFTextField
                  name="personalInfo.presentAddress.address"
                  label={`${Strings.addEmployee.address} Line 1`}
                  multiline
                />
                <RHFTextField
                  name="personalInfo.presentAddress.addressLine2"
                  label={`${Strings.addEmployee.address} Line 2`}
                  multiline
                />
              </Stack>
              <Stack direction={direction} spacing={2}>
                <RHFTextField
                  name="personalInfo.presentAddress.city"
                  label={`${Strings.addEmployee.city}`}
                  multiline
                />
                <RHFTextField
                  name="personalInfo.presentAddress.state"
                  label={`${Strings.addEmployee.state}`}
                  multiline
                />
              </Stack>
              <Stack direction={direction} spacing={2}>
                <RHFTextField
                  name="personalInfo.presentAddress.country"
                  label={`${Strings.addEmployee.country}`}
                  multiline
                />
                <RHFTextField
                  name="personalInfo.presentAddress.postalCode"
                  label={`${Strings.addEmployee.postalCode}`}
                  multiline
                />
              </Stack>
            </Stack>
            <Stack flex={1}>
              <Stack
                display="flex"
                flexDirection="row"
                width="100%"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography fontWeight={600} textAlign="center">
                  {Strings.addEmployee.permanentAddress}
                </Typography>
                <Stack>
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="samePresentAddress"
                        checked={disabled}
                        onChange={handleCheckboxChange}
                        inputProps={{ 'aria-label': 'Copy Address' }}
                      />
                    }
                    label={Strings.addEmployee.sameAsPresentAddress}
                  />
                </Stack>
              </Stack>
              <Stack gap={2}>
                <Stack direction={direction} spacing={2}>
                  <RHFTextField
                    name="personalInfo.permanentAddress.address"
                    label={`${Strings.addEmployee.address} Line 1`}
                    multiline
                    disabled={disabled}
                    InputLabelProps={{
                      shrink: !!methods.getValues('personalInfo.permanentAddress.address'),
                    }}
                  />
                  <RHFTextField
                    name="personalInfo.permanentAddress.addressLine2"
                    label={`${Strings.addEmployee.address} Line 2`}
                    multiline
                    disabled={disabled}
                    InputLabelProps={{
                      shrink: !!methods.getValues('personalInfo.permanentAddress.addressLine2'),
                    }}
                  />
                </Stack>
                <Stack direction={direction} spacing={2}>
                  <RHFTextField
                    name="personalInfo.permanentAddress.city"
                    label={`${Strings.addEmployee.city}`}
                    multiline
                    disabled={disabled}
                    InputLabelProps={{
                      shrink: !!methods.getValues('personalInfo.permanentAddress.city'),
                    }}
                  />
                  <RHFTextField
                    name="personalInfo.permanentAddress.state"
                    label={`${Strings.addEmployee.state}`}
                    multiline
                    disabled={disabled}
                    InputLabelProps={{
                      shrink: !!methods.getValues('personalInfo.permanentAddress.state'),
                    }}
                  />
                </Stack>
                <Stack direction={direction} spacing={2}>
                  <RHFTextField
                    name="personalInfo.permanentAddress.country"
                    label={`${Strings.addEmployee.country}`}
                    multiline
                    disabled={disabled}
                    InputLabelProps={{
                      shrink: !!methods.getValues('personalInfo.permanentAddress.country'),
                    }}
                  />
                  <RHFTextField
                    name="personalInfo.permanentAddress.postalCode"
                    label={`${Strings.addEmployee.postalCode}`}
                    multiline
                    disabled={disabled}
                    InputLabelProps={{
                      shrink: !!methods.getValues('personalInfo.permanentAddress.postalCode'),
                    }}
                  />
                </Stack>
              </Stack>
            </Stack>
          </Stack>
          <Stack>
            <Stack my={2} gap={1}>
              <Typography variant="h6">{Strings.addEmployee.emergency}</Typography>
            </Stack>
            <Stack mb={3}>
              <Divider />
            </Stack>
            {renderInputFieldSets()}
            <Stack flexDirection={'row'} justifyContent={'flex-end'} pt={2}>
              <Button
                variant="outlined"
                color="inherit"
                size="large"
                sx={{ width: 'max-content' }}
                onClick={handleAddMoreClick}
              >
                {Strings.button.addMore}
                <IconButton>
                  <Iconify icon="ic:outline-plus" />
                </IconButton>
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <EducationsInfoForm
        graduation={data?.education}
        updateGraduationState={updateGraduationState}
        labelSuffix
      />
      <WorkExperienceInfoForm labelSuffix workExperiences={data?.workExperience} />
      <DocumentsInfoForm title={title} data={data} filePickerName={filePickerName} />
      <BankInformationForm />
    </Stack>
  );
};

export { PersonalInfoForm };
